import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCurrentRole } from "../api";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:3001";

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    ""
  );
}

async function apiRequest(path, options = {}) {
  const token = getToken();

  const headers = {
    ...(options.headers || {}),
  };

  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status}`;

    try {
      const errorData = await response.json();

      errorMessage =
        errorData?.detail ||
        errorData?.message ||
        errorData?.error ||
        errorMessage;
    } catch (e) {
      // Ignore response parsing errors.
    }

    throw new Error(errorMessage);
  }

  const contentType =
    response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response;
}

function normalizeClient(raw) {
  if (!raw) return null;

  return {
    ...raw,
    id:
      raw.id ??
      raw.client_id ??
      raw.clientId ??
      null,
    full_name:
      raw.full_name ??
      raw.name ??
      [
        raw.first_name,
        raw.last_name,
      ]
        .filter(Boolean)
        .join(" "),
    email: raw.email ?? "",
    phone: raw.phone ?? "",
    birthday:
      raw.birthday ??
      raw.date_of_birth ??
      "",
    notes: raw.notes ?? "",
  };
}

function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled = false,
  style = {},
}) {
  const styles = {
    primary: {
      background:
        "linear-gradient(135deg, #ea580c, #f97316)",
      color: "white",
      border: "none",
    },
    secondary: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
    },
    ghost: {
      background: "white",
      color: "#334155",
      border: "1px solid #cbd5e1",
    },
    danger: {
      background: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fca5a5",
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "12px 16px",
        borderRadius: "14px",
        fontSize: "14px",
        fontWeight: "800",
        cursor: disabled
          ? "not-allowed"
          : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "0.2s ease",
        ...styles[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Input({
  label,
  ...props
}) {
  return (
    <label style={{ display: "block" }}>
      <div
        style={{
          fontSize: "14px",
          fontWeight: "700",
          color: "#334155",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>

      <input
        {...props}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "14px 16px",
          borderRadius: "14px",
          border: "1px solid #cbd5e1",
          outline: "none",
          fontSize: "15px",
          ...props.style,
        }}
      />
    </label>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    birthday: "",
    notes: "",
  });

  const role = String(
    getCurrentRole?.() || ""
  )
    .trim()
    .toLowerCase();

  const canManageClients =
    role === "admin" ||
    role === "manager";

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data =
        await apiRequest("/clients");

      const normalized =
        Array.isArray(data)
          ? data
              .map(normalizeClient)
              .filter(Boolean)
          : [];

      setClients(normalized);
    } catch (err) {
      setError(
        err?.message ||
          "Failed to load clients."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  async function handleCreateClient(e) {
    e.preventDefault();

    if (!canManageClients) {
      setError(
        "You do not have permission to create clients."
      );
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await apiRequest("/clients", {
        method: "POST",
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          birthday: form.birthday,
          notes: form.notes,
        }),
      });

      setForm({
        full_name: "",
        email: "",
        phone: "",
        birthday: "",
        notes: "",
      });

      setSuccess(
        "Client created successfully."
      );

      await loadClients();
    } catch (err) {
      setError(
        err?.message ||
          "Failed to create client."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteClient(
    clientId
  ) {
    if (!canManageClients) {
      setError(
        "You do not have permission to delete clients."
      );
      return;
    }

    const confirmed =
      window.confirm(
        "Delete this client?"
      );

    if (!confirmed) return;

    try {
      setError("");
      setSuccess("");

      await apiRequest(
        `/clients/${clientId}`,
        {
          method: "DELETE",
        }
      );

      setSuccess("Client deleted.");

      await loadClients();
    } catch (err) {
      setError(
        err?.message ||
          "Failed to delete client."
      );
    }
  }

  return (
    <div
      style={{
        padding: "24px",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#64748b",
              marginBottom: "6px",
            }}
          >
            Integrity Suite CRM
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "64px",
              lineHeight: 1,
              fontWeight: "900",
              color: "#0f172a",
            }}
          >
            Clients
          </h1>
        </div>

        {error ? (
          <div
            style={{
              background: "#fef2f2",
              border:
                "1px solid #fecaca",
              color: "#b91c1c",
              padding: "16px 18px",
              borderRadius: "18px",
              marginBottom: "18px",
              fontWeight: "700",
            }}
          >
            {error}
          </div>
        ) : null}

        {success ? (
          <div
            style={{
              background: "#ecfdf5",
              border:
                "1px solid #86efac",
              color: "#166534",
              padding: "16px 18px",
              borderRadius: "18px",
              marginBottom: "18px",
              fontWeight: "700",
            }}
          >
            {success}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              canManageClients
                ? "420px 1fr"
                : "1fr",
            gap: "20px",
            alignItems: "start",
          }}
        >
          {canManageClients ? (
            <div
              style={{
                background: "white",
                borderRadius: "24px",
                padding: "24px",
                boxShadow:
                  "0 12px 30px rgba(15,23,42,0.06)",
                border:
                  "1px solid #e2e8f0",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: "18px",
                  fontSize: "28px",
                  fontWeight: "800",
                  color: "#0f172a",
                }}
              >
                Add Client
              </h2>

              <form
                onSubmit={
                  handleCreateClient
                }
                style={{
                  display: "grid",
                  gap: "14px",
                }}
              >
                <Input
                  label="Full Name"
                  value={
                    form.full_name
                  }
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        full_name:
                          e.target.value,
                      })
                    )
                  }
                  required
                />

                <Input
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        email:
                          e.target.value,
                      })
                    )
                  }
                />

                <Input
                  label="Phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        phone:
                          e.target.value,
                      })
                    )
                  }
                />

                <Input
                  label="Birthday"
                  type="date"
                  value={form.birthday}
                  onChange={(e) =>
                    setForm(
                      (prev) => ({
                        ...prev,
                        birthday:
                          e.target.value,
                      })
                    )
                  }
                />

                <label
                  style={{
                    display: "block",
                  }}
                >
                  <div
                    style={{
                      fontSize:
                        "14px",
                      fontWeight:
                        "700",
                      color:
                        "#334155",
                      marginBottom:
                        "8px",
                    }}
                  >
                    Notes
                  </div>

                  <textarea
                    value={
                      form.notes
                    }
                    onChange={(e) =>
                      setForm(
                        (prev) => ({
                          ...prev,
                          notes:
                            e.target
                              .value,
                        })
                      )
                    }
                    style={{
                      width: "100%",
                      boxSizing:
                        "border-box",
                      minHeight:
                        "120px",
                      padding:
                        "14px 16px",
                      borderRadius:
                        "14px",
                      border:
                        "1px solid #cbd5e1",
                      outline: "none",
                      fontSize:
                        "15px",
                      resize:
                        "vertical",
                    }}
                  />

                </label>

                <Button
                  type="submit"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : "Save Client"}
                </Button>
              </form>
            </div>
          ) : null}

          <div
            style={{
              background: "white",
              borderRadius: "24px",
              padding: "24px",
              boxShadow:
                "0 12px 30px rgba(15,23,42,0.06)",
              border:
                "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                marginBottom: "18px",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: "800",
                    color: "#0f172a",
                  }}
                >
                  Client List
                </h2>

                {!canManageClients ? (
                  <div
                    style={{
                      marginTop:
                        "6px",
                      color:
                        "#64748b",
                      fontSize:
                        "14px",
                    }}
                  >
                    Open client
                    records to manage
                    assigned credit
                    workflows.
                  </div>
                ) : null}
              </div>

              <Button
                variant="ghost"
                onClick={
                  loadClients
                }
              >
                Refresh
              </Button>
            </div>

            {loading ? (
              <div
                style={{
                  padding: "24px",
                  borderRadius:
                    "18px",
                  background:
                    "#f8fafc",
                  color:
                    "#475569",
                  fontWeight:
                    "700",
                }}
              >
                Loading clients...
              </div>
            ) : clients.length ===
              0 ? (
              <div
                style={{
                  padding: "24px",
                  borderRadius:
                    "18px",
                  background:
                    "#f8fafc",
                  color:
                    "#64748b",
                  fontWeight:
                    "700",
                }}
              >
                No clients yet.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gap: "14px",
                }}
              >
                {clients.map(
                  (client) => {
                    const clientId =
                      client.id ||
                      client.client_id ||
                      client.clientId;

                    return (
                      <div
                        key={clientId}
                        style={{
                          border:
                            "1px solid #e2e8f0",
                          borderRadius:
                            "18px",
                          padding:
                            "18px",
                          background:
                            "#ffffff",
                        }}
                      >
                        <div
                          style={{
                            display:
                              "flex",
                            justifyContent:
                              "space-between",
                            gap: "16px",
                            alignItems:
                              "flex-start",
                            flexWrap:
                              "wrap",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize:
                                  "22px",
                                fontWeight:
                                  "800",
                                color:
                                  "#0f172a",
                                marginBottom:
                                  "8px",
                              }}
                            >
                              {client.full_name ||
                                "Unnamed Client"}
                            </div>

                            <div
                              style={{
                                fontSize:
                                  "15px",
                                color:
                                  "#64748b",
                                lineHeight:
                                  1.7,
                              }}
                            >
                              <div>
                                Email:{" "}
                                {client.email ||
                                  "—"}
                              </div>

                              <div>
                                Phone:{" "}
                                {client.phone ||
                                  "—"}
                              </div>

                              <div>
                                ID:{" "}
                                {clientId ||
                                  "—"}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display:
                                "flex",
                              gap: "10px",
                              flexWrap:
                                "wrap",
                            }}
                          >
                            {clientId ? (
                              <Link
                                to={`/clients/${clientId}`}
                                style={{
                                  display:
                                    "inline-block",
                                  padding:
                                    "12px 16px",
                                  borderRadius:
                                    "14px",
                                  fontSize:
                                    "14px",
                                  fontWeight:
                                    "800",
                                  textDecoration:
                                    "none",
                                  background:
                                    "#fff7ed",
                                  color:
                                    "#c2410c",
                                  border:
                                    "1px solid #fdba74",
                                }}
                              >
                                Open
                              </Link>
                            ) : (
                              <span
                                style={{
                                  display:
                                    "inline-block",
                                  padding:
                                    "12px 16px",
                                  borderRadius:
                                    "14px",
                                  fontSize:
                                    "14px",
                                  fontWeight:
                                    "800",
                                  background:
                                    "#f1f5f9",
                                  color:
                                    "#64748b",
                                  border:
                                    "1px solid #cbd5e1",
                                }}
                              >
                                Missing ID
                              </span>
                            )}

                            {canManageClients ? (
                              <Button
                                variant="danger"
                                onClick={() =>
                                  handleDeleteClient(
                                    clientId
                                  )
                                }
                                disabled={
                                  !clientId
                                }
                              >
                                Delete
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
